import { Add, Delete, Edit, Settings, TrendingDown, TrendingUp, Sort as SortIcon, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { Box, Button, Card, CardContent, Chip, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, Grid, IconButton, InputLabel, Link, MenuItem, Paper, Select, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';
import { navigate } from '@reach/router';
import { useEffect, useMemo, useState } from 'react';
import apiClient from '../api/client';
import AdvancedFilters from '../components/AdvancedFilters';
import AsyncSearchSelect from '../components/AsyncSearchSelect';
import AsyncTableSearch from '../components/AsyncTableSearch';
import ExcelImportDialog from '../components/ExcelImportDialog';
import Layout from '../components/Layout';
import SchemaEditor from '../components/SchemaEditor';
import StoragePickerDialog from '../components/StoragePickerDialog';
import StyledTableContainer from '../components/StyledTableContainer';
import { useAuth } from '../contexts/AuthContext';
import { useRealtimeSSE } from '../hooks/useRealtimeSSE';

const ExpenseStatusChip = ({ status }) => {
  const statusMap = {
    'pending': { label: 'В ожидании', color: 'warning' },
    'completed': { label: 'Завершён', color: 'success' },
    'failed': { label: 'Не выполнен', color: 'error' }
  };
  const statusInfo = statusMap[status] || { label: status, color: 'default' };
  return <Chip label={statusInfo.label} color={statusInfo.color} size="small" />;
};

const ExpensesPage = () => {
  const { user, loading: authLoading } = useAuth();
  useRealtimeSSE();

  const [expenses, setExpenses] = useState([]);
  const [filteredExpenses, setFilteredExpenses] = useState([]);
  const [deals, setDeals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [advancedFilters, setAdvancedFilters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [formData, setFormData] = useState({
    deal_id: '', amount: '', payment_method: '', status: 'pending', payment_date: ''
  });

  // Пагинация
  const [page, setPage] = useState(1);
  const perPage = 20;
  const [totalPages, setTotalPages] = useState(null);

  // Поиск
  const [searchActive, setSearchActive] = useState(false);
  const [searchPage, setSearchPage] = useState(1);
  const [searchPagination, setSearchPagination] = useState({ page: 1, pages: 1, total: 0, perPage: 20 });

  // Сортировка
  const [sortBy, setSortBy] = useState('payment_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // Кастомные поля
  const [expenseSchema, setExpenseSchema] = useState([]);
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false);
  const [presignedMap, setPresignedMap] = useState({});
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerImagesOnly, setPickerImagesOnly] = useState(false);
  const [pickerTargetField, setPickerTargetField] = useState(null);

  // Импорт
  const [importOpen, setImportOpen] = useState(false);

  // Поля для фильтров
  const baseFilterFields = [
    { value: 'amount', label: 'Сумма' },
    { value: 'payment_method', label: 'Метод оплаты' },
    { value: 'status', label: 'Статус' },
    { value: 'payment_date', label: 'Дата' },
  ];

  const allFilterFields = useMemo(() => [
    ...baseFilterFields,
    ...expenseSchema.map(f => ({ value: f.name, label: f.name }))
  ], [expenseSchema]);

  // Статистика
  const statistics = useMemo(() => {
    const data = searchActive ? filteredExpenses : expenses;

    const total = data.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);
    const completed = data.filter(e => e.status === 'completed');
    const pending = data.filter(e => e.status === 'pending');
    const failed = data.filter(e => e.status === 'failed');
    const completedSum = completed.reduce((sum, exp) => sum + (Number(exp.amount) || 0), 0);

    return {
      total: total,
      count: data.length,
      completed: completed.length,
      completedSum: completedSum,
      pending: pending.length,
      failed: failed.length,
      average: data.length > 0 ? total / data.length : 0
    };
  }, [expenses, filteredExpenses, searchActive]);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/auth');
      return;
    }
    if (user) {
      fetchExpenses();
      fetchDeals();
      fetchSchema();
    }
  }, [user, authLoading]);

  // Локальная фильтрация (только при неактивном серверном поиске)
  useEffect(() => {
    if (searchActive) return;

    if (expenses.length > 0) {
      let filtered = expenses;

      // Применяем расширенные фильтры
      if (advancedFilters.length > 0) {
        filtered = filtered.filter(expense => {
          return advancedFilters.every(filter => {
            const value = expense[filter.field];
            if (value === null || value === undefined) return false;

            const stringValue = String(value).toLowerCase();
            const filterValue = filter.value.toLowerCase();

            switch (filter.operator) {
              case 'contains':
                return stringValue.includes(filterValue);
              case 'equals':
                return stringValue === filterValue;
              case 'startsWith':
                return stringValue.startsWith(filterValue);
              case 'endsWith':
                return stringValue.endsWith(filterValue);
              case 'greaterThan':
                return Number(value) > Number(filter.value);
              case 'lessThan':
                return Number(value) < Number(filter.value);
              default:
                return true;
            }
          });
        });
      }

      // Применяем сортировку
      filtered = filtered.sort((a, b) => {
        let aValue = a[sortBy];
        let bValue = b[sortBy];

        // Обработка вложенных полей (customFields)
        if (sortBy.includes('customFields.')) {
          const field = sortBy.replace('customFields.', '');
          aValue = a.customFields?.[field];
          bValue = b.customFields?.[field];
        }

        // Обработка пустых значений
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;

        // Сравнение
        if (typeof aValue === 'string') {
          aValue = aValue.toLowerCase();
          bValue = bValue?.toLowerCase() || '';
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });

      setFilteredExpenses(filtered);
    } else {
      setFilteredExpenses([]);
    }
  }, [expenses, advancedFilters, searchActive, sortBy, sortOrder]);

  const fetchDeals = async () => {
    try {
      const response = await apiClient.get('/deals');
      setDeals(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Ошибка при загрузке сделок:', err);
    }
  };

  const fetchExpenses = async (pageParam = page) => {
    try {
      setLoading(true);
      const response = await apiClient.get(`/expenses?page=${pageParam}&per_page=${perPage}`);

      const expensesData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
      setExpenses(expensesData);

      const pagination = response.pagination || response.data || {};
      const pages = pagination.pages || pagination.total_pages || 1;
      setTotalPages(pages);

      setError('');
    } catch (err) {
      console.error('Ошибка при загрузке расходов:', err);
      setError('Не удалось загрузить список расходов');
    } finally {
      setLoading(false);
    }
  };

  const fetchSchema = async () => {
    try {
      const resp = await apiClient.get('/expenses/schema');
      setExpenseSchema(resp.data.schema || []);
    } catch (err) {
      console.error('Не удалось получить схему расходов', err);
    }
  };

  const getDealTitle = (dealId) => {
    if (!dealId) return '—';
    const deal = deals.find(d => d.id === dealId || d._id === dealId);
    return deal ? deal.title : 'Неизвестная сделка';
  };

  const handleOpenDialog = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      const dynamicValues = {};
      expenseSchema.forEach(f => {
        dynamicValues[f.name] = expense[f.name] ?? '';
      });
      setFormData({
        amount: expense.amount.toString(),
        payment_date: new Date(expense.payment_date).toISOString().substr(0, 10),
        payment_method: expense.payment_method,
        status: expense.status,
        deal_id: expense.deal_id.toString(),
        ...dynamicValues
      });
    } else {
      setEditingExpense(null);
      const dynamicDefaults = {};
      expenseSchema.forEach(f => {
        dynamicDefaults[f.name] = f.default ?? '';
      });
      setFormData({
        amount: '',
        payment_date: new Date().toISOString().substr(0, 10),
        payment_method: 'card',
        status: 'pending',
        deal_id: deals.length > 0 ? (deals[0].id || deals[0]._id).toString() : '',
        ...dynamicDefaults
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      amount: parseFloat(formData.amount),
      deal_id: formData.deal_id
    };
    try {
      if (editingExpense) {
        await apiClient.put(`/expenses/${editingExpense.id || editingExpense._id}`, data);
      } else {
        await apiClient.post('/expenses', data);
      }
      handleCloseDialog();
      fetchExpenses();
    } catch (err) {
      console.error('Ошибка при сохранении расхода:', err);
      setError('Не удалось сохранить данные расхода');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот расход?')) {
      try {
        await apiClient.delete(`/expenses/${id}`);
        fetchExpenses();
      } catch (err) {
        console.error('Ошибка при удалении расхода:', err);
        setError('Не удалось удалить расход');
      }
    }
  };

  const handleApplyFilters = (filters) => {
    setAdvancedFilters(filters);
  };

  const handleResetFilters = () => setAdvancedFilters([]);

  const handlePageChange = (e, value) => {
    if (searchActive) {
      setSearchPage(value);
    } else {
      setPage(value);
      fetchExpenses(value);
    }
  };

  // Callbacks для поиска
  const handleSearchResults = (items) => {
    setFilteredExpenses(items);
  };

  const handleSearchPaginationChange = (paginationInfo) => {
    setSearchPagination(paginationInfo);
  };

  const handleSearchActiveChange = (active) => {
    setSearchActive(active);
    if (!active) {
      setFilteredExpenses(expenses);
      setSearchPage(1);
      setSearchPagination({ page: 1, pages: 1, total: 0, perPage: 20 });
    }
  };

  // Схема
  const handleSaveSchema = async (newSchema) => {
    try {
      await apiClient.put('/expenses/schema', { schema: newSchema });
      setExpenseSchema(newSchema);
      setSchemaDialogOpen(false);
      fetchExpenses();
    } catch (err) {
      console.error('Ошибка при сохранении схемы:', err);
      alert('Не удалось сохранить схему');
    }
  };

  // Storage
  const uploadForField = async (fieldName, fileObj) => {
    try {
      const form = new FormData();
      form.append('file', fileObj);
      const uploadResp = await apiClient.post('/storage/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const key = uploadResp.data.key || uploadResp.data.file_key;
      setFormData(prev => ({ ...prev, [fieldName]: key }));
      alert('Файл загружен');
    } catch (err) {
      console.error(err);
      alert('Ошибка загрузки');
    }
  };

  const fetchPresigned = async (key) => {
    if (!key || presignedMap[key]) return presignedMap[key];
    try {
      const resp = await apiClient.get(`/storage/files/${encodeURIComponent(key)}/url`);
      const url = resp.data.url;
      setPresignedMap(prev => ({ ...prev, [key]: url }));
      return url;
    } catch (err) {
      console.error(err);
    }
    return null;
  };

  const renderFileField = (field, isImage) => {
    const key = formData[field.name];
    const fileName = key ? String(key).split('/').pop() : '';
    return (
      <Box key={field.name} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{field.name}</Typography>
        {key && (
          <Box sx={{ mb: 1 }}>
            {isImage ? (
              <img
                alt={field.name}
                src={presignedMap[key] || ''}
                style={{ maxWidth: 200, maxHeight: 150, objectFit: 'cover' }}
                onLoad={async () => { if (!presignedMap[key]) await fetchPresigned(key); }}
              />
            ) : (
              <Link
                component="button"
                variant="body2"
                onClick={async () => {
                  const url = await fetchPresigned(key);
                  if (url) window.open(url, '_blank');
                }}
              >
                {fileName}
              </Link>
            )}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setPickerTargetField(field.name);
              setPickerImagesOnly(isImage);
              setPickerOpen(true);
            }}
          >
            Выбрать из хранилища
          </Button>
          <Button
            size="small"
            variant="outlined"
            component="label"
          >
            Загрузить
            <input
              type="file"
              hidden
              accept={isImage ? 'image/*' : '*'}
              onChange={(e) => { if (e.target.files[0]) uploadForField(field.name, e.target.files[0]); }}
            />
          </Button>
        </Box>
      </Box>
    );
  };

  const displayData = searchActive ? filteredExpenses : filteredExpenses;

  return (
    <Layout>
      <Box sx={{ p: 3, background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.2)', boxShadow: '0 8px 32px 0 rgba(31,38,135,0.37)' }}>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' }, justifyContent: { sm: 'space-between' }, gap: { xs: 2, sm: 0 } }}>
          <Typography variant="h4" component="h1" sx={{ color: 'white' }}>
            Расходы и платежи
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, width: { xs: '100%', sm: 'auto' }, gap: { xs: 1, sm: 2 } }}>
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => setSchemaDialogOpen(true)}
              sx={{ width: { xs: '100%', sm: 'auto' }, color: 'white', borderColor: 'white' }}
            >
              Настроить поля
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Добавить расход
            </Button>
          </Box>
        </Box>

        {/* Статистика */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(33, 150, 243, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  Общая сумма
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.total.toLocaleString()} ₸
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {statistics.count} расходов
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(76, 175, 80, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingUp /> Завершено
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.completed}
                </Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {statistics.completedSum.toLocaleString()} ₸
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(255, 152, 0, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                  В ожидании
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.pending}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(244, 67, 54, 0.2)', borderRadius: 2 }}>
              <CardContent>
                <Typography variant="h6" sx={{ color: 'white', mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TrendingDown /> Не выполнено
                </Typography>
                <Typography variant="h4" sx={{ color: 'white' }}>
                  {statistics.failed}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Поиск и фильтры */}
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
          <AsyncTableSearch
            value={searchQuery}
            onChange={setSearchQuery}
            onResults={handleSearchResults}
            onActiveChange={handleSearchActiveChange}
            onPaginationChange={handleSearchPaginationChange}
            placeholder="Поиск расходов..."
            searchDoc="payments"
            searchFields={["amount", "payment_method", "status"]}
            page={searchPage}
            perPage={perPage}
            sx={{ flex: 1 }}
          />
          <AdvancedFilters
            filters={advancedFilters}
            onApply={handleApplyFilters}
            onReset={handleResetFilters}
            availableFields={allFilterFields}
          />
          {/* Сортировка */}
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Сортировать по</InputLabel>
            <Select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              label="Сортировать по"
              size="small"
              sx={{
                color: 'white',
                '.MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.3)' },
                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255, 255, 255, 0.5)' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'white' },
                '.MuiSvgIcon-root': { color: 'white' }
              }}
            >
              <MenuItem value="payment_date">Дата платежа</MenuItem>
              <MenuItem value="amount">Сумма</MenuItem>
              <MenuItem value="payment_method">Метод оплаты</MenuItem>
              <MenuItem value="status">Статус</MenuItem>
              <MenuItem value="deal_id">ID Сделки</MenuItem>
            </Select>
          </FormControl>

          <IconButton
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            sx={{
              color: 'white',
              bgcolor: 'rgba(255, 255, 255, 0.1)',
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.2)' }
            }}
          >
            {sortOrder === 'asc' ? <ArrowUpward /> : <ArrowDownward />}
          </IconButton>
        </Box>

        {error && (
          <Paper sx={{ p: 2, mb: 2, bgcolor: 'error.light', color: 'error.contrastText' }}>
            {error}
          </Paper>
        )}

        {/* Таблица */}
        <StyledTableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: 'white' }}>Сделка</TableCell>
                <TableCell sx={{ color: 'white' }}>Сумма</TableCell>
                <TableCell sx={{ color: 'white' }}>Дата</TableCell>
                <TableCell sx={{ color: 'white' }}>Метод</TableCell>
                <TableCell sx={{ color: 'white' }}>Статус</TableCell>
                {expenseSchema.map(field => (
                  <TableCell key={field.name} sx={{ color: 'white' }}>{field.name}</TableCell>
                ))}
                <TableCell sx={{ color: 'white', position: 'sticky', right: 0, background: 'rgba(30,30,30,0.8)', zIndex: 2 }}>
                  Действия
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6 + expenseSchema.length} align="center" sx={{ color: 'white' }}>
                    Загрузка...
                  </TableCell>
                </TableRow>
              ) : displayData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6 + expenseSchema.length} align="center" sx={{ color: 'white' }}>
                    {searchQuery || advancedFilters.length > 0 ? 'Расходы не найдены' : 'Нет расходов'}
                  </TableCell>
                </TableRow>
              ) : (
                displayData.map(exp => (
                  <TableRow key={exp.id || exp._id}>
                    <TableCell sx={{ color: 'white' }}>{getDealTitle(exp.deal_id)}</TableCell>
                    <TableCell sx={{ color: 'white' }}>{Number(exp.amount).toLocaleString()} ₸</TableCell>
                    <TableCell sx={{ color: 'white' }}>
                      {new Date(exp.payment_date).toLocaleDateString('ru-RU')}
                    </TableCell>
                    <TableCell sx={{ color: 'white' }}>{exp.payment_method}</TableCell>
                    <TableCell><ExpenseStatusChip status={exp.status} /></TableCell>
                    {expenseSchema.map(field => (
                      <TableCell key={field.name} sx={{ color: 'white' }}>
                        {field.type === 'boolean' ? (
                          exp[field.name] ? 'Да' : 'Нет'
                        ) : field.type === 'image' ? (
                          exp[field.name] ? (
                            <img
                              alt={field.name}
                              src={presignedMap[exp[field.name]] || ''}
                              style={{ maxWidth: 80, maxHeight: 60, objectFit: 'cover' }}
                              onMouseEnter={async () => { if (!presignedMap[exp[field.name]]) await fetchPresigned(exp[field.name]); }}
                            />
                          ) : '—'
                        ) : field.type === 'file' ? (
                          exp[field.name] ? (
                            <Link
                              component="button"
                              variant="body2"
                              onClick={async () => {
                                const url = await fetchPresigned(exp[field.name]);
                                if (url) window.open(url, '_blank');
                              }}
                            >
                              {String(exp[field.name]).split('/').pop()}
                            </Link>
                          ) : '—'
                        ) : (
                          exp[field.name] ?? '—'
                        )}
                      </TableCell>
                    ))}
                    <TableCell sx={{ position: 'sticky', right: 0, background: 'rgba(30,30,30,0.7)', zIndex: 1 }}>
                      <IconButton onClick={() => handleOpenDialog(exp)}>
                        <Edit sx={{ color: 'white' }} />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(exp.id || exp._id)}>
                        <Delete sx={{ color: 'white' }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </StyledTableContainer>

        {/* Пагинация */}
        {(() => {
          const currentPage = searchActive ? searchPagination.page : page;
          const currentTotalPages = searchActive ? searchPagination.pages : totalPages;
          const currentTotal = searchActive ? searchPagination.total : null;

          if (currentTotalPages <= 1 && !searchActive) return null;

          return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 2, gap: 2, flexWrap: 'wrap' }}>
              {searchActive && currentTotal !== null && (
                <Typography variant="body2" color="text.secondary" sx={{ color: 'white' }}>
                  Найдено: {currentTotal}
                </Typography>
              )}
              {currentTotalPages > 1 && (
                <Pagination
                  color="primary"
                  count={currentTotalPages}
                  page={currentPage}
                  onChange={handlePageChange}
                />
              )}
            </Box>
          );
        })()}
      </Box>

      {/* Диалог создания/редактирования */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>{editingExpense ? 'Редактировать расход' : 'Добавить расход'}</DialogTitle>
        <form onSubmit={handleSubmit}>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Сделка (необязательно)</InputLabel>
              <Select name="deal_id" value={formData.deal_id} onChange={handleChange}>
                <MenuItem value="">Без сделки</MenuItem>
                {deals.map(d => (
                  <MenuItem key={d.id || d._id} value={d.id || d._id}>
                    {d.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Сумма"
              name="amount"
              type="number"
              value={formData.amount}
              onChange={handleChange}
              margin="normal"
              InputProps={{ inputProps: { min: 0, step: "0.01" } }}
              required
            />
            <TextField
              fullWidth
              label="Дата"
              name="payment_date"
              type="date"
              value={formData.payment_date}
              onChange={handleChange}
              margin="normal"
              required
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Метод</InputLabel>
              <Select name="payment_method" value={formData.payment_method} onChange={handleChange}>
                <MenuItem value="card">Банковская карта</MenuItem>
                <MenuItem value="cash">Наличные</MenuItem>
                <MenuItem value="bank_transfer">Банковский перевод</MenuItem>
                <MenuItem value="online">Онлайн платеж</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal">
              <InputLabel>Статус</InputLabel>
              <Select name="status" value={formData.status} onChange={handleChange}>
                <MenuItem value="pending">В ожидании</MenuItem>
                <MenuItem value="completed">Завершён</MenuItem>
                <MenuItem value="failed">Не выполнен</MenuItem>
              </Select>
            </FormControl>

            {/* Кастомные поля */}
            {expenseSchema.map(field => {
              if (field.type === 'text') {
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={field.name}
                    name={field.name}
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    margin="normal"
                  />
                );
              } else if (field.type === 'number') {
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={field.name}
                    name={field.name}
                    type="number"
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    margin="normal"
                  />
                );
              } else if (field.type === 'date') {
                return (
                  <TextField
                    key={field.name}
                    fullWidth
                    label={field.name}
                    name={field.name}
                    type="date"
                    value={formData[field.name] || ''}
                    onChange={handleChange}
                    margin="normal"
                    InputLabelProps={{ shrink: true }}
                  />
                );
              } else if (field.type === 'boolean') {
                return (
                  <FormControl key={field.name} fullWidth margin="normal">
                    <InputLabel>{field.name}</InputLabel>
                    <Select
                      name={field.name}
                      value={formData[field.name] !== undefined ? String(formData[field.name]) : 'false'}
                      onChange={(e) => setFormData(prev => ({ ...prev, [field.name]: e.target.value === 'true' }))}
                    >
                      <MenuItem value="false">Нет</MenuItem>
                      <MenuItem value="true">Да</MenuItem>
                    </Select>
                  </FormControl>
                );
              } else if (field.type === 'select') {
                return (
                  <FormControl key={field.name} fullWidth margin="normal">
                    <InputLabel>{field.name}</InputLabel>
                    <Select
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleChange}
                    >
                      {(field.options || []).map(opt => (
                        <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                );
              } else if (field.type === 'file') {
                return renderFileField(field, false);
              } else if (field.type === 'image') {
                return renderFileField(field, true);
              }
              return null;
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Отмена</Button>
            <Button type="submit" variant="contained">Сохранить</Button>
          </DialogActions>
        </form>
      </Dialog>

      {/* Диалог схемы */}
      <Dialog open={schemaDialogOpen} onClose={() => setSchemaDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Настроить поля расходов</DialogTitle>
        <DialogContent>
          <SchemaEditor
            schema={expenseSchema}
            onChange={(newSchema) => setExpenseSchema(newSchema)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchemaDialogOpen(false)}>Отмена</Button>
          <Button
            variant="contained"
            onClick={() => handleSaveSchema(expenseSchema)}
          >
            Сохранить
          </Button>
        </DialogActions>
      </Dialog>

      {/* Storage Picker */}
      <StoragePickerDialog
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        onSelect={(key) => {
          if (pickerTargetField) {
            setFormData(prev => ({ ...prev, [pickerTargetField]: key }));
          }
          setPickerOpen(false);
        }}
        imagesOnly={pickerImagesOnly}
      />

      {/* Excel Import */}
      <ExcelImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImport={async (data) => {
          try {
            await apiClient.post('/expenses/import', { data });
            setImportOpen(false);
            fetchExpenses();
          } catch (err) {
            console.error(err);
            alert('Ошибка импорта');
          }
        }}
      />
    </Layout>
  );
};

export default ExpensesPage;
